import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import type { NoteType, NoteData } from './notes';

// Parent-portal API. Mirrors server shapes from
// [server/src/routes/portal.js]. Dual-audience: a parent JWT is auto-scoped
// to its single client (no clientId param needed); staff with `view_portal`
// pass ?clientId=<uuid> to preview any of their accessible clients.

export interface PortalSession {
    id: string;
    clientId: string;
    clientName: string;
    staffName: string;
    date: string | null;
    startH: number;
    startM: number;
    dur: number;
    label: string;
    status: string;
    noteStatus: string;
    location: string;
    cancelledBy: 'staff' | 'parent' | null;
    parentSignedAt: string | null;
}

export interface PortalClientHeader {
    id: string;
    first: string;
    last: string;
    dob: string | null;
    status: string;
}

export interface PortalOverview {
    client: PortalClientHeader;
    upcomingSessions: PortalSession[];
    pendingSignatures: { notes: number; plans: number };
    activePlan: { id: string; title: string; status: 'Active' | 'Signed'; signedAt: string | null } | null;
    unreadMessages: number;
    /** Unpaid invoice count (Issued + Partial). Always 0 when
     *  `features.portalInvoicesEnabled` is false. */
    unpaidInvoices: number;
    features: {
        /** Per-practice toggle (settings.billing.portalInvoicesEnabled).
         *  Defaults to true when unset. Drives whether the Invoices tab
         *  is rendered. */
        portalInvoicesEnabled: boolean;
        /** Per-practice toggle (settings.billing.portalPaymentsEnabled).
         *  Defaults to true when unset. Drives whether the Pay Now
         *  button mounts on unpaid invoices. Phase 5 of
         *  docs/build-plans/pma-stripe-payments.md adds the settings UI. */
        portalPaymentsEnabled: boolean;
    };
}

export interface PortalInvoiceSummary {
    id: string;
    displayId: string;
    status: 'Issued' | 'Partial' | 'Paid';
    issuedAt: string | null;
    dueDate: string | null;
    subtotal: number;
    paidAmount: number;
    balance: number;
    deliveryMethod: 'portal' | 'mail' | 'manual' | null;
    deliveredAt: string | null;
    /** Stamped by the Phase 3 webhook on payment_intent.succeeded.
     *  Distinct from `status` so the portal can hide its Pay Now button
     *  before the bookkeeping side reconciles the invoice. */
    paidInPortalAt: string | null;
}

export interface PortalInvoiceLine {
    id: string;
    claimDisplayId: string;
    dos: string | null;
    serviceCode: string;
    units: number;
    unitRate: number;
    amountSnapshot: number;
    prSourceClaimId: string | null;
    prSourceDisplayId: string;
    prSourceDos: string | null;
}

export interface PortalInvoiceDetail extends PortalInvoiceSummary {
    remitToName: string;
    remitToAddress: string;
    paymentInstructions: string;
    terms: string;
    footerNote: string;
    /** Cumulative dollars refunded through the parent portal — Phase 3
     *  webhook flips invoices.refunded_in_portal_cents on charge.refunded;
     *  Phase 6 surfaces it on the parent-portal detail page. 0 when the
     *  invoice has had no refunds. */
    refundedInPortal: number;
    /** Phase 8 — per-invoice autopay opt-in state. Drives the
     *  Autopay toggle on the parent-portal detail page; the daily
     *  scheduler reads invoices.autopay_enabled to pick candidates. */
    autopayEnabled: boolean;
    /** Phase 9 — cents currently in-flight through ACH bank settlement
     *  (sum of invoice_payments.amount_cents where status='processing').
     *  0 when nothing is pending. Drives the "Pending settlement —
     *  funds typically arrive in 3-5 business days" banner; the parent
     *  sees their ACH charge isn't lost even though the invoice still
     *  reads as unpaid until the bank settles. */
    pendingSettlementCents: number;
    lines: PortalInvoiceLine[];
}

export interface PortalPlanBehavior {
    id: string;
    name: string;
    status: string;
    behaviorType: string;
}

export interface PortalPlanTarget {
    id: string;
    name: string;
    description: string;
    dataType: string;
    masteryDirection: string | null;
    masteryValue: number | null;
    masterySessions: number | null;
    trials: number | null;
    chainingMethod: string | null;
    status: string;
}

export interface PortalPlanProgram {
    id: string;
    name: string;
    status: string;
    teachingMethod: string;
    programDomain: string;
    targets: PortalPlanTarget[];
}

export interface PortalPlanCustomField {
    defId: string;
    label: string;
    type: 'text' | 'textarea' | 'date' | 'number' | 'select' | 'multiselect' | 'checkbox';
    value: unknown;
}

export interface PortalPendingPlan {
    id: string;
    title: string;
    startDate: string | null;
    reviewDate: string | null;
    notes: string;
    sentToPortalAt: string | null;
    bcbaName: string;
    bcbaSignedAt: string | null;
    bcbaSignedName: string;
    behaviors: PortalPlanBehavior[];
    programs: PortalPlanProgram[];
    customFields: PortalPlanCustomField[];
}

export interface PortalPendingSignatures {
    notes: PortalSession[];
    plans: PortalPendingPlan[];
}

export interface PortalPlan {
    id: string;
    title: string;
    status: 'Active' | 'Signed' | 'Draft';
    startDate: string | null;
    reviewDate: string | null;
    notes: string;
    sentToPortalAt: string | null;
    bcbaName: string;
    signedAtBcba: string | null;
    signedNameBcba: string;
    signedAtParent: string | null;
    signedNameParent: string;
    signedRelationshipParent: string;
    behaviors: PortalPlanBehavior[];
    programs: PortalPlanProgram[];
    customFields: PortalPlanCustomField[];
}

export interface PortalSessionNote extends PortalSession {
    noteData: NoteData | null;
    notes: string;
    noteType: NoteType | null;
}

export type PortalDaysFilter = 7 | 30 | 'all';

function clientParam(clientId?: string): Record<string, string> | undefined {
    return clientId ? { clientId } : undefined;
}

async function fetchOverview(clientId?: string): Promise<PortalOverview> {
    const res = await api.get<PortalOverview>('/portal/overview', { params: clientParam(clientId) });
    return res.data;
}

export function usePortalOverview(clientId?: string) {
    return useQuery({
        queryKey: ['portal', 'overview', clientId ?? 'self'] as const,
        queryFn: () => fetchOverview(clientId),
        staleTime: 15_000,
    });
}

async function fetchSessions(
    days: PortalDaysFilter,
    clientId?: string,
): Promise<PortalSession[]> {
    const params: Record<string, string> = { days: String(days) };
    if (clientId) params.clientId = clientId;
    const res = await api.get<PortalSession[]>('/portal/sessions', { params });
    return res.data;
}

export function usePortalSessions(days: PortalDaysFilter, clientId?: string) {
    return useQuery({
        queryKey: ['portal', 'sessions', clientId ?? 'self', days] as const,
        queryFn: () => fetchSessions(days, clientId),
        staleTime: 15_000,
    });
}

async function fetchSessionNote(id: string, clientId?: string): Promise<PortalSessionNote> {
    const res = await api.get<PortalSessionNote>(`/portal/session-note/${id}`, {
        params: clientParam(clientId),
    });
    return res.data;
}

export function usePortalSessionNote(id: string | undefined, clientId?: string) {
    return useQuery({
        queryKey: ['portal', 'session-note', clientId ?? 'self', id] as const,
        queryFn: () => fetchSessionNote(id!, clientId),
        enabled: !!id,
    });
}

/** Aggregate pending-signature counts, scoped server-side to the caller's
 *  client visibility (admin → all; assigned → assigned; parent → own). */
export interface PendingSignaturesSummary {
    notes: number;
    plans: number;
}

async function fetchPendingSignaturesSummary(): Promise<PendingSignaturesSummary> {
    const res = await api.get<PendingSignaturesSummary>('/portal/pending-signatures-summary');
    return res.data;
}

export function usePendingSignaturesSummary(options?: { enabled?: boolean }) {
    return useQuery({
        queryKey: ['portal', 'pending-signatures-summary'] as const,
        queryFn: fetchPendingSignaturesSummary,
        staleTime: 30_000,
        enabled: options?.enabled ?? true,
    });
}

async function fetchPendingSignatures(clientId?: string): Promise<PortalPendingSignatures> {
    const res = await api.get<PortalPendingSignatures>('/portal/pending-signatures', {
        params: clientParam(clientId),
    });
    return res.data;
}

export function usePortalPendingSignatures(clientId?: string) {
    return useQuery({
        queryKey: ['portal', 'pending-signatures', clientId ?? 'self'] as const,
        queryFn: () => fetchPendingSignatures(clientId),
        staleTime: 15_000,
    });
}

async function fetchPlans(clientId?: string): Promise<PortalPlan[]> {
    const res = await api.get<PortalPlan[]>('/portal/plan', { params: clientParam(clientId) });
    return res.data;
}

export function usePortalPlans(clientId?: string) {
    return useQuery({
        queryKey: ['portal', 'plan', clientId ?? 'self'] as const,
        queryFn: () => fetchPlans(clientId),
        staleTime: 30_000,
    });
}

async function fetchPortalInvoices(clientId?: string): Promise<PortalInvoiceSummary[]> {
    const res = await api.get<PortalInvoiceSummary[]>('/portal/invoices', {
        params: clientParam(clientId),
    });
    return res.data;
}

export function usePortalInvoices(clientId: string | undefined, enabled: boolean) {
    return useQuery({
        queryKey: ['portal', 'invoices', clientId ?? 'self'] as const,
        queryFn: () => fetchPortalInvoices(clientId),
        staleTime: 30_000,
        enabled,
    });
}

async function fetchPortalInvoice(id: string, clientId?: string): Promise<PortalInvoiceDetail> {
    const res = await api.get<PortalInvoiceDetail>(`/portal/invoices/${id}`, {
        params: clientParam(clientId),
    });
    return res.data;
}

export function usePortalInvoice(
    id: string | undefined,
    clientId?: string,
    options: { refetchInterval?: number | false } = {},
) {
    // refetchInterval lets the saved-card pay flow poll for the
    // webhook-driven invoice update (paid_amount roll-forward) for a
    // short window after the parent submits payment. Pass false (the
    // useQuery default) outside that window so we don't burn requests.
    return useQuery({
        queryKey: ['portal', 'invoices', clientId ?? 'self', id] as const,
        queryFn: () => fetchPortalInvoice(id!, clientId),
        enabled: !!id,
        refetchInterval: options.refetchInterval ?? false,
    });
}

/** Fetches the server-rendered .docx and triggers a browser download.
 *  The server emits the audit entry and (for parents) stamps delivery. */
export function useDownloadPortalInvoiceDocx() {
    return useMutation({
        mutationFn: async ({
            id,
            displayId,
            clientId,
        }: {
            id: string;
            displayId?: string;
            clientId?: string;
        }): Promise<{ filename: string }> => {
            const res = await api.get(`/portal/invoices/${id}/docx`, {
                params: clientParam(clientId),
                responseType: 'blob',
            });

            const cd = res.headers['content-disposition'] as string | undefined;
            const match = cd && /filename="([^"]+)"/.exec(cd);
            const filename = match ? match[1] : `${displayId || 'invoice'}.docx`;

            const objectUrl = URL.createObjectURL(res.data as Blob);
            const a = document.createElement('a');
            a.href = objectUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(objectUrl);

            return { filename };
        },
    });
}

// ── Parent-only mutations ─────────────────────────────────────────────────────
// All three require a parent JWT — staff with `view_portal` get 403.

export interface PortalSignaturePayload {
    signerName: string;
    /** Specific relationship captured at sign time — may differ from the
     *  parent account's general relationship. */
    relationship: string;
    /** Base64 PNG data URL; server strips the `data:` prefix. */
    signatureImage: string;
}

export function useSignSessionNote() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, payload }: { id: string; payload: PortalSignaturePayload }) => {
            const res = await api.post<{ ok: true }>(`/portal/sessions/${id}/sign`, payload);
            return res.data;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['portal'] });
        },
    });
}

export function useCancelSession() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, cancelReasonId }: { id: string; cancelReasonId?: string }) => {
            const res = await api.post<{ ok: true }>(`/portal/sessions/${id}/cancel`, {
                cancelReasonId,
            });
            return res.data;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['portal'] });
        },
    });
}

export function useSignPortalPlan() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, payload }: { id: string; payload: PortalSignaturePayload }) => {
            const res = await api.post<{ ok: true }>(`/portal/tx-plans/${id}/sign`, payload);
            return res.data;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['portal'] });
        },
    });
}
